<template>
    <span>
        <b-table
            ref="table"
            striped
            hover
            responsive
            selectable
            select-mode="single"
            bordered
            head-variant="light"
            :sticky-header="btableMaxHeight"
            no-border-collapse
            style="overflow-x: auto"
            :items="myProvider"
            :fields="fields"
            :busy.sync="loading"
            :current-page="options.currentPage"
            :per-page="options.perPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            tbody-tr-class="vt-table-rows"
            v-if="tableType === 'vectraits'"
        >
            <!-- A custom formatted header cell to allow for the displaying of header tooltips -->
            <template v-slot:head()="field">
            <span
                :title="field.field.title"
                style="font-size: small; white-space: nowrap"
            >
                {{ field.label }}
            </span>
            </template>
            <!-- add tooltips for each cell -->
            <!-- RIGHT NOW this is only working on columns with simple output. If the value contains some kind 
                of composit value like the SpeciesName column on the VecDyn Explorer, the tooltip doesn't show up.-->
            <template v-slot:cell()="data">
            <span :title="data.value">{{ data.value }}</span>
            </template>
            <!-- Special rendering for the "Variables" column. The content of this field comes from the back end 
                as an array. By default, it would just be displayed as an array. This allows us to display each 
                element with a comma between. -->
            <template v-slot:cell(Variables)="data">
            <span
                :title="data.value[0] + ', ' + data.value[1]"
                v-if="data.value[1]"
            >
                {{ data.value[0] }}, {{ data.value[1] }}
            </span>
            <span :title="data.value[0]" v-else>
                {{ data.value[0] }}
            </span>
            </template>
            <!-- Special rendering for the "DOI" column: remove protocol from URL -->
            <template v-slot:cell(DOI)="data">
            <span :title="data.value">
                {{ doiString(data.value) }}
            </span>
            </template>
            <!-- Replace rows with a busy spinner while the table data is loading. -->
            <!-- https://bootstrap-vue.org/docs/components/table#table-busy-state -->
            <template #table-busy>
            <div class="text-danger my-2">
                <b-spinner class="align-left"></b-spinner>
                <strong>&nbsp;Loading...</strong>
            </div>
            </template>
        </b-table>
        <b-table
            ref="table"
            striped
            hover
            responsive
            selectable
            select-mode="single"
            bordered
            head-variant="light"
            :sticky-header="btableMaxHeight"
            no-border-collapse
            style="overflow-x: auto"
            :items="myProvider"
            :fields="fields"
            :busy.sync="loading"
            :current-page="options.currentPage"
            :per-page="options.perPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            tbody-tr-class="vt-table-rows"
            v-else-if="tableType === 'vecdyn'"
        >
            <template v-slot:head()="field">
            <span
                :title="field.field.title"
                style="font-size: small; white-space: nowrap"
            >
                {{ field.label }}
            </span>
            </template>
            <template v-slot:cell(SpeciesName)="data">
                <span v-for="(item, index) in data.value" :key="index">
                    {{ item }}<span v-if="index != data.value.length - 1">, </span>
                </span>
            </template>
            <template v-slot:cell(Years)="data">
                <span v-for="index in data.value" :key="index">
                    {{ index }} <br>
                </span>
            </template>
            <template v-slot:cell(CollectionMethods)="data">
                <span v-for="index in data.value" :key="index">
                    {{ index }} <br>
                </span>
            </template>
            <template #table-busy>
            <div class="text-danger my-2">
                <b-spinner class="align-left"></b-spinner>
                <strong>&nbsp;Loading...</strong>
            </div>
            </template>
            <template v-slot:cell()="data">
                <span :title='data.value'>{{ data.value}}</span>
            </template>
        </b-table>
        <b-pagination
            v-if="options.totalRows"
            v-model="options.currentPage"
            :total-rows="options.totalRows"
            :per-page="options.perPage"
            size="sm"
            class="my-0 mb-2 "
        ></b-pagination>
        <h5 v-else>No datasets uploaded yet.</h5>
    </span>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

export default {
    props: {
        tableType: String,
    },
    created (){
        this.fieldDefs = this.getFieldDefs().then(() => {
            this.fields = this.buildFieldsArray(this.fieldDefs);
        }); 
    },
    data () {
        return {
            datasets: [],
            fields: [], // Dynamically populated by buildFieldsArray(). Fed to the b-table to determine what columns show.
            fieldDefs: [], // Populated by getFieldDefs(). Stores the definition objects for each VecTraits Column.
            options: {
                // used by the table pagination object and the display of total rows at top of table
                currentPage: 1,
                totalRows: 0,
                perPage: 50,
            },
            fieldArray: [], // array of column arguments from the 'Select a column' fields
            operatorArray: [], // array of operator arguments from the 'Select an operator' fields
            termArray: [], // array of search arguments from the 'Enter a search term...' fields
            keywords: "", // space delimited list of search terms from the 'Search by phrase...' field
            sortDesc: false, // sort decending?
            fieldDropdownOptions: [], // this is now being built dynamically with buildOptions()
            operatorOptionsArray: [],
            sortBy: "DatasetID", // by default, sort by 'ID'
            title: "",
            loading: false,
            allRowIDs: [], // array that holds all IDs of current query results. This is passed from the back end.
            btableMaxHeight: "600px",
        }
    },
    watch: {
        view: function () {
        this.$refs.table.refresh();
        },
        "options.currentPage"() {
        this.addUrlQueryParams();
        },
        sortBy: function () {
        this.addUrlQueryParams();
        },
        sortDesc: function () {
        this.addUrlQueryParams();
        }
    },
    methods: { 
        myProvider() {
            const that = this;
            const endpoint = that.tableType === 'vectraits' ? "/api/user/vectraits-datasets/" : '/api/user/vecdyn-datasets/';

            return HTTP.get(endpoint)
            .then((response) => {
                that.datasets = response.data.data.results;
                that.options.totalRows = parseInt(response.data.data.count);
                that.allRowIDs = response.data.ids;
                this.csvEndpoint = response.request.responseURL;

                return that.datasets;
            })
            .catch((error) => {
                this.err_msg = HandleErrors.HandleErrors(error);
                this.apiErrModalShow = true;
                return [];
            });
        },
        getFieldDefs() {
            const that = this;
            return HTTP.get(`/api/column-definitions/`)
            .then((response) => {
                that.fieldDefs = response.data;
                })
            .catch((error) => {
                this.err_msg = HandleErrors.HandleErrors(error);
                this.apiErrModalShow = true;
            });
        },
        buildFieldsArray(defs) {
            // Gets called by the created() lifecycle hook. Build the fields array which is used by the main b-table
            // NOTE: IF THERE ARE NO ROWS IN DATA, THIS WILL ERROR OUT.
            // TODO: handle no rows in input. Currently, we are checking that there are rows before calling this
            // function in myProvider().
            let def_dict = {};
            defs.forEach(function (obj) {
                def_dict[obj.field_name] = obj.definition;
            });
            return this.tableType === 'vectraits' ? [
                {
                key: "DatasetID",
                sortable: true,
                tdClass: "text-truncate vt-table-cell text-underline",
                title: def_dict["DatasetID".toLowerCase()],
                },
                {
                key: "OriginalTraitName",
                sortable: true,
                tdClass: "text-truncate vt-table-cell-wide",
                title: def_dict["OriginalTraitName".toLowerCase()],
                },
                {
                key: "Variables",
                sortable: true,
                tdClass: "text-truncate vt-table-cell-wide",
                title: def_dict["Variables".toLowerCase()],
                },
                {
                key: "Interactor1Stage",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["Interactor1Stage".toLowerCase()],
                },
                {
                key: "Interactor1Genus",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["Interactor1Genus".toLowerCase()],
                },
                {
                key: "Interactor1Species",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["Interactor1Species".toLowerCase()],
                },
                {
                key: "Interactor2Genus",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["Interactor2Genus".toLowerCase()],
                },
                {
                key: "Interactor2Species",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["Interactor2Species".toLowerCase()],
                },
                {
                key: "Citation",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["Citation".toLowerCase()],
                },
                {
                key: "DOI",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["DOI".toLowerCase()],
                },
                {
                key: "CuratedByDOI",
                sortable: true,
                tdClass: "text-truncate vt-table-cell",
                title: def_dict["CuratedByDOI".toLowerCase()],
                },
            ] : [
                    {key:"SpeciesName", sortable:true, tdClass:"vt-table-cell-wide",},
                    {key:"Title", sortable:true, tdClass:"vt-table-cell-wide",},
                    {key:"Collections", sortable:true, tdClass:"vt-table-cell",},
                    {key:"Years", sortable:true, tdClass:"vt-table-cell",},
                    {key:"CollectionMethods", sortable:true, tdClass:"vt-table-cell",},
                ];
        },
        doiString: function (doi) {
            // Remove the protocol prefix from the DOI and return the remaining substring.
            if (doi == null) {
                return null;
            }
            if (doi.startsWith("http://")) {
                return doi.replace("http://", "");
            } else if (doi.startsWith("https://")) {
                return doi.replace("https://", "");
            }
            return doi;
        },
    }
}
</script>